import { useFormValues } from '@retainerclub/hooks';
import { Datepicker } from 'flowbite-react';
import PropTypes from 'prop-types';
import { get, useFormContext } from 'react-hook-form';
import { parseDateOnlyString } from '../../../transforms';
import FormErrors from '../FieldContainer/FormErrors';

function parseDateValue(value, isDateOnly = false) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'string') {
    // For date-only fields, parse at local midnight to avoid timezone issues
    if (isDateOnly) {
      return parseDateOnlyString(value);
    }
    // For datetime fields, parse ISO string normally
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date;
    }
  }

  return null;
}

function DatePicker({
  name,
  isFutureDate = false,
  includeTodayInFuture = false,
  title = null,
  label = '',
  required = false,
}) {
  const {
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();
  const values = useFormValues();

  const parsedVal = get(values, name);
  const hasError = get(errors, name);

  // Check if this is a date-only field (ISO8601Date from GraphQL)
  // Date-only fields end with specific patterns or are known date-only fields
  const isDateOnly =
    name.includes('partnerUploadScanAt') || name.includes('planStartDelay');

  // Convert string dates to Date objects for the DatePicker
  const dateValue = parseDateValue(parsedVal, isDateOnly);

  // Determine minDate based on date picker type
  let minDate;
  let maxDate;

  if (isFutureDate) {
    if (includeTodayInFuture) {
      // Allow today's date by setting minDate to today at midnight
      const today = new Date();
      minDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      );
    } else {
      // Default behavior: tomorrow onwards
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      minDate = tomorrow;
    }
    maxDate = new Date('2030-01-01');
  } else {
    // For past dates, allow anything from 2021 onwards
    minDate = new Date('2021-01-01');
    maxDate = new Date();
  }

  function handleChange(date) {
    setValue(name, date, { shouldValidate: true, shouldDirty: true });
    trigger(name);
  }

  return (
    <div>
      <label
        className="mb-2 block font-medium text-black/70 text-sm capitalize leading-6"
        htmlFor={name}
      >
        {label || name}
        {required && <span className="text-red-400">*</span>}
      </label>
      <Datepicker
        key={dateValue ? dateValue.getTime() : `empty-${name}`}
        id={name}
        name={name}
        title={title}
        value={dateValue ?? null}
        onChange={handleChange}
        minDate={minDate}
        maxDate={maxDate}
        autoHide={true}
        showClearButton={false}
        showTodayButton={false}
        weekStart={1}
        theme={{
          root: {
            base: 'relative',
          },
          input: {
            field: {
              input: {
                base: `block w-full rounded-lg border text-sm ${
                  hasError
                    ? 'border-red-300 bg-red-50 text-red-900 placeholder:text-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 bg-gray-50 text-gray-900 focus:border-rcprimary-400 focus:ring-rcprimary-400'
                }`,
              },
            },
          },
          popup: {
            root: {
              base: 'absolute top-10 z-50 block pt-2',
              inline: 'relative top-0 z-auto',
              inner: 'inline-block rounded-lg bg-white p-4 shadow-lg',
            },
            header: {
              base: '',
              title: 'px-2 py-3 text-center font-semibold text-gray-900',
              selectors: {
                base: 'mb-2 flex justify-between',
                button: {
                  base: 'rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200',
                  prev: '',
                  next: '',
                  view: '',
                },
              },
            },
            view: {
              base: 'p-1',
            },
            footer: {
              base: 'mt-2 flex space-x-2',
              button: {
                base: 'w-full rounded-lg px-5 py-2 text-center text-sm font-medium focus:ring-4 focus:ring-blue-300',
                today: 'bg-blue-700 text-white hover:bg-blue-800',
                clear:
                  'border border-gray-300 bg-white text-gray-900 hover:bg-gray-100',
              },
            },
          },
          views: {
            days: {
              header: {
                base: 'mb-1 grid grid-cols-7',
                title:
                  'h-6 text-center text-sm font-medium leading-6 text-gray-500',
              },
              items: {
                base: 'grid grid-cols-7',
                item: {
                  base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                  selected:
                    'bg-rcprimary-400 text-white hover:bg-rcprimary-500',
                  disabled:
                    'text-gray-500 opacity-50 cursor-not-allowed hover:bg-transparent',
                },
              },
            },
            months: {
              items: {
                base: 'grid grid-cols-4',
                item: {
                  base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                  selected:
                    'bg-rcprimary-400 text-white hover:bg-rcprimary-500',
                  disabled:
                    'text-gray-500 opacity-50 cursor-not-allowed hover:bg-transparent',
                },
              },
            },
            years: {
              items: {
                base: 'grid grid-cols-4',
                item: {
                  base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                  selected:
                    'bg-rcprimary-400 text-white hover:bg-rcprimary-500',
                  disabled:
                    'text-gray-500 opacity-50 cursor-not-allowed hover:bg-transparent',
                },
              },
            },
            decades: {
              items: {
                base: 'grid grid-cols-4',
                item: {
                  base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                  selected:
                    'bg-rcprimary-400 text-white hover:bg-rcprimary-500',
                  disabled:
                    'text-gray-500 opacity-50 cursor-not-allowed hover:bg-transparent',
                },
              },
            },
          },
        }}
      />
      <FormErrors name={name} errors={errors} />
    </div>
  );
}

DatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  isFutureDate: PropTypes.bool,
  includeTodayInFuture: PropTypes.bool,
  title: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
};

export default DatePicker;
